import { toDate } from 'date-fns-tz';
import {
  addDays,
  addHours,
  addMinutes,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  isValid
} from 'date-fns';
import { getStoreLocation } from './index';

/**
 * Parse the date from DB (SQL format) into JS date, add TimeZone info based on Shop url
 * @param {string} date -  SQL like date string, e.g. 2017-05-15 09:12:34 (YYYY-MM-DD hh:mm:ss), no timezone info attached
 * @param {string} location - location overrides
 * @returns {null | Date}
 */
export const parseSqlDate = (date, location) => {
  if (!date) return null;

  let timeZone;
  const storeLocation = location || getStoreLocation();

  switch (storeLocation) {
    case 'NZ':
      timeZone = 'Pacific/Auckland';
      break;
    case 'US':
      timeZone = 'America/Los_Angeles';
      break;
    default:
      timeZone = 'Australia/Brisbane';
  }

  const jsDate = toDate(date, { timeZone });
  if (!isValid(jsDate)) {
    // eslint-disable-next-line no-console
    console.error('Unparsable date', date);
    return null;
  }

  return jsDate;
};

/**
 * Given a date object and see if it is a future date
 * @param {Date} date - js date object
 * @returns {boolean}
 */
export const isFutureDate = date => (date > new Date());

/**
 * Given a date string and see if it is a future date
 * @param {string} date -  SQL like date string, e.g. 2017-05-15 09:12:34 (YYYY-MM-DD hh:mm:ss), no timezone info attached
 * @returns {boolean}
 */
export const isFutureDateString = date => (isFutureDate(parseSqlDate(date)));

/**
 * Get human readable durations between 2 dates, stops at days.
 * @param {Date} dateAfter - date that is later than the dateBefore
 * @param {Date} dateBefore - date that is earlier than the dateAfter
 * @returns {null|{hours: number | *, seconds: number | *, minutes: number | *, days: number | *}}
 */
export const getDuration = (dateAfter, dateBefore = new Date()) => {
  if (!dateAfter || dateAfter <= dateBefore) return null;

  let tempDate;
  const days = differenceInDays(dateAfter, dateBefore);
  tempDate = addDays(dateBefore, days);
  const hours = differenceInHours(dateAfter, tempDate);
  tempDate = addHours(tempDate, hours);
  const minutes = differenceInMinutes(dateAfter, tempDate);
  tempDate = addMinutes(tempDate, minutes);
  const seconds = differenceInSeconds(dateAfter, tempDate);

  const clockNumber = (x) => String(Math.round(x)).padStart(2, '0');

  return {
    days: clockNumber(days || 0),
    hours: clockNumber(hours || 0),
    minutes: clockNumber(minutes || 0),
    seconds: clockNumber(seconds || 0)
  };
};

/**
 * Util to change an OZ date like DD/MM/YYYY to other locales date format, right now just US
 * @param ozDateString - OZ date string, example: DD/MM/YYYY
 * @param location - location overrides
 * @returns {string} converted date string
 */
export const formatOzDate = (ozDateString, location) => {
  const storeLocation = location || getStoreLocation();
  const [day, month, year] = ozDateString.split('/');

  switch (storeLocation) {
    case 'US':
      return `${month}/${day}/${year}`;
    default:
      return ozDateString;
  }
};

/**
 * returns: + if target date > today's date
 *          - if target date < today's date
 * @param targetDate
 */
export const diffDate = (targetDate) => {
  const date = parseSqlDate(targetDate);
  const today = new Date();
  const diff = (date.getTime()) - (today.getTime());
  return diff;
};
